import React, { useMemo, useState } from "react";
import { Clock, Calculator, Plus, Minus } from "lucide-react";

export const DoseCalculator = ({ dc, fallbackText }) => {
    const isKit = dc.kit_size && dc.kit_size > 1;
    const unitLabel = dc.unit === "IU" ? " IU" : dc.unit;
    const [dose, setDose] = useState(dc.default_dose);
    const [freqDays, setFreqDays] = useState(dc.default_freq_days);
    const [vialQty, setVialQty] = useState(1);

    const calc = useMemo(() => {
        const d = parseFloat(dose);
        const q = Math.max(1, parseInt(vialQty) || 1);
        if (!d || d <= 0) return null;

        // For bulk kits, each "unit" purchased is `kit_size` vials, so multiply effective vial-mg by kit_size.
        const unitsPerVial = dc.vial_amount / d;
        const daysPerVial = unitsPerVial * freqDays;
        const vialsPerOrder = isKit ? dc.kit_size * q : q;
        const totalDays = daysPerVial * vialsPerOrder;
        const totalWeeks = totalDays / 7;

        const formatDuration = (days) => {
            if (days < 21) {
                return { val: days < 10 ? days.toFixed(1) : Math.round(days), unit: "days" };
            }
            const weeks = days / 7;
            return { val: weeks >= 2 ? Math.round(weeks) : weeks.toFixed(1), unit: "weeks" };
        };

        const per = formatDuration(daysPerVial);
        const perKit = formatDuration(daysPerVial * (dc.kit_size || 1));
        const total = formatDuration(totalDays);
        return {
            dosesPerVial: unitsPerVial.toFixed(unitsPerVial < 10 ? 1 : 0),
            perVial: per,
            perKit,
            total,
            totalDaysRaw: Math.round(totalDays),
            totalWeeksRaw: totalWeeks,
            vialQty: q,
            vialsPerOrder,
        };
    }, [dose, freqDays, vialQty, dc.vial_amount, dc.kit_size, isKit]);

    const presets = [
        { d: 1, f: 7, label: "1mg weekly" },
        { d: 2, f: 7, label: "2mg weekly" },
        { d: 4, f: 7, label: "4mg weekly" },
    ];

    return (
        <div className="mt-6 p-5 border border-neon/30 bg-neon/5 relative" data-testid="dose-calculator">
            <div className="flex items-start gap-3">
                <Calculator className="w-5 h-5 text-neon shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="flex-1">
                    <p className="overline text-neon mb-1">HOW LONG WILL IT LAST YOU?</p>
                    <p className="text-xs text-ink-dim">Enter your research protocol and vial quantity — see exactly how many weeks your order covers.</p>

                    <div className="grid grid-cols-[1fr_1.5fr] gap-3 mt-5">
                        <label className="block">
                            <span className="overline text-[9px] text-ink-mute mb-1.5 block">YOUR DOSE</span>
                            <div className="flex items-stretch border border-ink-strong bg-ink-base">
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    value={dose}
                                    onChange={(e) => setDose(e.target.value)}
                                    className="w-full px-3 py-2.5 bg-transparent text-ink text-sm font-mono focus:outline-none"
                                    data-testid="dose-input"
                                />
                                <span className="px-3 flex items-center bg-ink-surface text-ink-dim text-xs font-mono border-l border-ink-strong">{dc.unit}</span>
                            </div>
                        </label>
                        <label className="block">
                            <span className="overline text-[9px] text-ink-mute mb-1.5 block">FREQUENCY</span>
                            <select
                                value={freqDays}
                                onChange={(e) => setFreqDays(parseFloat(e.target.value))}
                                className="w-full border border-ink-strong bg-ink-base text-ink px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-neon"
                                data-testid="dose-freq"
                            >
                                {dc.freq_options.map((o) => (
                                    <option key={o.label} value={o.days}>{o.label}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {/* Vial qty scaler */}
                    <div className="mt-3">
                        <span className="overline text-[9px] text-ink-mute mb-1.5 block">{isKit ? `KITS · 1 KIT = ${dc.kit_size} VIALS` : "VIALS"}</span>
                        <div className="flex items-stretch w-fit border border-ink-strong bg-ink-base" data-testid="dose-vial-qty">
                            <button type="button" onClick={() => setVialQty(Math.max(1, vialQty - 1))} className="px-3 text-ink-dim hover:text-neon" data-testid="vial-qty-dec"><Minus className="w-3.5 h-3.5" /></button>
                            <input
                                type="number"
                                min="1"
                                value={vialQty}
                                onChange={(e) => setVialQty(parseInt(e.target.value) || 1)}
                                className="w-16 px-2 py-2 bg-transparent text-ink text-sm font-mono font-semibold text-center focus:outline-none"
                                data-testid="vial-qty-input"
                            />
                            <button type="button" onClick={() => setVialQty(vialQty + 1)} className="px-3 text-ink-dim hover:text-neon" data-testid="vial-qty-inc"><Plus className="w-3.5 h-3.5" /></button>
                            {(isKit ? [1, 2, 3] : [3, 5, 10]).map((n) => (
                                <button key={n} type="button" onClick={() => setVialQty(n)} className="px-3 border-l border-ink-strong text-[10px] font-mono text-ink-dim hover:text-neon" data-testid={`vial-qty-preset-${n}`}>
                                    {n}×
                                </button>
                            ))}
                        </div>
                    </div>

                    {dc.unit === "mg" && dc.vial_amount >= 10 && (
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="dose-presets">
                            {presets.map((p) => (
                                <button
                                    key={p.label}
                                    type="button"
                                    onClick={() => { setDose(p.d); setFreqDays(p.f); }}
                                    className="text-[10px] font-mono uppercase tracking-[0.1em] border border-ink-strong hover:border-neon hover:text-neon text-ink-dim px-2 py-1 transition-colors"
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {calc && (
                        <div className="mt-5 pt-5 border-t border-ink-strong space-y-4" data-testid="dose-result">
                            {/* Per-vial line */}
                            <div className="flex items-baseline gap-3">
                                <Clock className="w-4 h-4 text-ink-dim shrink-0" strokeWidth={1.5} />
                                <p className="text-xs text-ink-mute">Each {dc.vial_amount}{unitLabel} vial lasts</p>
                                <p className="font-mono text-sm text-ink-dim" data-testid="dose-per-vial">
                                    <span className="text-ink font-bold">{calc.perVial.val}</span> {calc.perVial.unit}
                                </p>
                            </div>

                            {/* Per-kit line for bulk kits */}
                            {isKit && (
                                <div className="flex items-baseline gap-3">
                                    <Clock className="w-4 h-4 text-gold shrink-0" strokeWidth={1.5} />
                                    <p className="text-xs text-ink-mute">One kit ({dc.kit_size} × {dc.vial_amount}{unitLabel}) lasts</p>
                                    <p className="font-mono text-sm text-gold" data-testid="dose-per-kit">
                                        <span className="font-bold">{calc.perKit.val}</span> {calc.perKit.unit}
                                    </p>
                                </div>
                            )}

                            {/* Total headline */}
                            <div>
                                <div className="flex items-baseline gap-3">
                                    <Clock className="w-4 h-4 text-neon shrink-0" strokeWidth={1.5} />
                                    <p className="text-sm text-ink-dim">
                                        {isKit ? (
                                            <>Your <span className="text-ink font-semibold">{calc.vialQty} × kit ({calc.vialsPerOrder} vials)</span> order lasts</>
                                        ) : calc.vialQty > 1 ? (
                                            <>Your <span className="text-ink font-semibold">{calc.vialQty} × {dc.vial_amount}{unitLabel}</span> order lasts</>
                                        ) : (
                                            <>Your <span className="text-ink font-semibold">{dc.vial_amount}{unitLabel}</span> vial lasts</>
                                        )}
                                    </p>
                                </div>
                                <p className="mt-2 font-display">
                                    <span className="text-5xl font-bold text-neon" data-testid="dose-result-value">{calc.total.val}</span>
                                    <span className="ml-2 text-xl text-ink-dim">{calc.total.unit}</span>
                                </p>
                                <p className="mt-2 font-mono text-[11px] text-ink-mute">
                                    ≈ {calc.totalDaysRaw} days · {calc.dosesPerVial} doses per vial
                                </p>
                            </div>

                            {/* Bulk hint — hide for already-bulk kits */}
                            {!isKit && calc.vialQty < 10 && (
                                <div className="pt-3 border-t border-ink text-[11px] font-mono text-ink-mute" data-testid="dose-bulk-hint">
                                    💡 <span className="text-ink-dim">Tip:</span> ordering more unlocks bundle discounts — 3 vials 10% off · 5 vials 15% · 10 vials 20%.
                                </div>
                            )}
                        </div>
                    )}

                    {!calc && fallbackText && (
                        <p className="mt-4 text-xs text-ink-dim">{fallbackText}</p>
                    )}
                </div>
            </div>
        </div>
    );
};
